import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStochasticInvoicesAction,
  resetStochasticInvoices,
} from "../slices/stochasticInvoicesSlice";
import { RootState } from "../../../../../app/rootReducer";
import { FetchStochasticInvoicesRequest } from "../../../../../api/fetchStochasticInvoices/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";

export function useStochasticInvoices() {
  const dispatch = useDispatch();
  const { invoices, totalCount, loading, error } = useSelector(
    (state: RootState) => state.stochasticInvoices,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Default sort by invoicedAt descending (newest first)
  const [sorting, setSorting] = useState<SortingState>([
    { id: "invoicedAt", desc: true },
  ]);

  const [filters, setFilters] = useState<{
    searchTerm?: string;
  }>({
    searchTerm: "",
  });

  useEffect(() => {
    const requestData: FetchStochasticInvoicesRequest = {
      page: pagination.pageIndex + 1,
      sortBy: sorting.length > 0 ? sorting[0].id : "invoicedAt",
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : "desc",
      searchTerm: filters.searchTerm,
      pageSize: pagination.pageSize,
    };
    dispatch(fetchStochasticInvoicesAction(requestData));
  }, [dispatch, pagination, sorting, filters]);

  useEffect(() => {
    return () => {
      dispatch(resetStochasticInvoices());
    };
  }, [dispatch]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (searchTerm: string) => {
    setFilters({ searchTerm });
  };

  return {
    invoices,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
